/**
 * Version Search Utilities
 *
 * Provides client-side filtering for versions using an AND-based search syntax.
 * Users can search by file name, system tags, or user tags, combining terms with '+'.
 *
 * @example
 * // Single term - matches file name, system tag, or any user tag
 * "YR25" -> matches versions containing "YR25" in name or tags
 *
 * // Multiple terms with AND logic
 * "YR25+wk40" -> matches versions containing BOTH "YR25" AND "wk40"
 *
 * // Terms can match across name and tags
 * "sales+Q4" -> file name contains "sales" AND a tag contains "Q4"
 */

import type {
  ParsedSearchQuery,
  VersionOption,
  VersionSearchResult,
} from './types';

// ============================================================================
// Constants
// ============================================================================

/** Default placeholder text for the search input */
export const VERSION_SEARCH_PLACEHOLDER = 'Search name or tags (+ for AND)';

/** System tag values */
export const SYSTEM_TAG_CURRENT = 'current';
export const SYSTEM_TAG_PREVIOUS = 'previous';

// ============================================================================
// Search Query Parsing
// ============================================================================

/**
 * Parse a search input string into individual terms.
 *
 * Handles the '+' AND operator by splitting the input and cleaning each term.
 * Empty terms are ignored, making inputs like "YR25+" or "++YR25++" valid.
 *
 * @param input - Raw search input from user
 * @returns Parsed query with normalized terms
 *
 * @example
 * parseSearchQuery("YR25+wk40") -> { terms: ["yr25", "wk40"], isEmpty: false }
 * parseSearchQuery("  YR25 + wk40  ") -> { terms: ["yr25", "wk40"], isEmpty: false }
 * parseSearchQuery("") -> { terms: [], isEmpty: true }
 */
export function parseSearchQuery(input: string): ParsedSearchQuery {
  const terms = input
    .split('+')
    .map((term) => term.trim().toLowerCase())
    .filter((term) => term.length > 0);

  return {
    terms,
    isEmpty: terms.length === 0,
  };
}

// ============================================================================
// Version Text Extraction
// ============================================================================

/**
 * Build a searchable text string from a version.
 *
 * Combines the file name, system tag, and all user tag names into a single
 * lowercase string for efficient searching.
 *
 * @param version - Version to extract searchable text from
 * @returns Lowercase string containing all searchable fields
 */
export function getSearchableText(version: VersionOption): string {
  const parts: string[] = [];

  // Add file name
  if (version.file_name) {
    parts.push(version.file_name);
  }

  // Add system tag
  if (version.system_tag) {
    parts.push(version.system_tag);
  }

  // Add all user tag names
  if (version.tags && version.tags.length > 0) {
    for (const tag of version.tags) {
      if (tag.name) {
        parts.push(tag.name);
      }
    }
  }

  // Add version ID (for SHA search)
  parts.push(version.id);

  return parts.join(' ').toLowerCase();
}

// ============================================================================
// Version Matching
// ============================================================================

/**
 * Check if a version matches all search terms (AND logic).
 *
 * Each term must be found somewhere in the version's searchable text.
 * All terms must match for the version to be considered a match.
 *
 * @param version - Version to check
 * @param terms - Array of lowercase search terms (all must match)
 * @returns True if version matches all terms
 */
export function versionMatchesTerms(
  version: VersionOption,
  terms: string[]
): boolean {
  if (terms.length === 0) {
    return true;
  }

  const searchableText = getSearchableText(version);
  return terms.every((term) => searchableText.includes(term));
}

// ============================================================================
// Main Filter Function
// ============================================================================

/**
 * Filter versions based on a search query string.
 *
 * This is the main entry point for version search. It parses the query,
 * applies AND logic for multiple terms, and returns matching versions.
 *
 * @param versions - Array of versions to filter
 * @param searchQuery - Raw search input (e.g., "YR25+wk40")
 * @returns Search result with filtered versions and metadata
 */
export function filterVersions(
  versions: VersionOption[],
  searchQuery: string
): VersionSearchResult {
  const { terms, isEmpty } = parseSearchQuery(searchQuery);

  if (isEmpty) {
    return {
      filtered: versions,
      total: versions.length,
      isFiltered: false,
    };
  }

  const filtered = versions.filter((version) =>
    versionMatchesTerms(version, terms)
  );

  return {
    filtered,
    total: versions.length,
    isFiltered: true,
  };
}

// ============================================================================
// Version Helpers
// ============================================================================

/**
 * Find the version with the 'current' system tag
 */
export function findCurrentVersion(
  versions: VersionOption[]
): VersionOption | undefined {
  return versions.find((v) => v.system_tag === SYSTEM_TAG_CURRENT);
}

/**
 * Find the version with the 'previous' system tag
 */
export function findPreviousVersion(
  versions: VersionOption[]
): VersionOption | undefined {
  return versions.find((v) => v.system_tag === SYSTEM_TAG_PREVIOUS);
}

/**
 * Sort versions by creation date (newest first)
 */
export function sortVersionsByDate(versions: VersionOption[]): VersionOption[] {
  return [...versions].sort((a, b) => {
    if (!a.created_at || !b.created_at) return 0;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

/**
 * Format a date string to absolute format (e.g., "Nov 15, 2025")
 */
export function formatVersionDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format version display name as "V[X]"
 */
export function formatVersionName(version: VersionOption): string {
  if (version.version_number !== undefined) {
    return `V${version.version_number}`;
  }
  return getShortSha(version.id);
}

/**
 * Get the short SHA (first 8 characters) of a version ID
 */
export function getShortSha(id: string, length = 8): string {
  return id.substring(0, length);
}

/**
 * Find the latest version (most recently created)
 */
export function findLatestVersion(
  versions: VersionOption[]
): VersionOption | undefined {
  if (versions.length === 0) return undefined;
  return sortVersionsByDate(versions)[0];
}

/**
 * Resolve a selected value to the actual version it represents.
 *
 * Handles system tags (current/previous) with fallback to latest version:
 * - "current" -> version with system_tag='current', or latest if none
 * - "previous" -> version with system_tag='previous', or latest if none
 * - version ID -> the specific version
 *
 * @param value - Selected value (system tag or version ID)
 * @param versions - Array of available versions
 * @returns The resolved version and whether it's a fallback
 */
export function resolveSelectedVersion(
  value: string,
  versions: VersionOption[]
): { version: VersionOption | undefined; isFallback: boolean } {
  if (value === SYSTEM_TAG_CURRENT) {
    const current = findCurrentVersion(versions);
    if (current) {
      return { version: current, isFallback: false };
    }
    // Fallback to latest
    return { version: findLatestVersion(versions), isFallback: true };
  }

  if (value === SYSTEM_TAG_PREVIOUS) {
    const previous = findPreviousVersion(versions);
    if (previous) {
      return { version: previous, isFallback: false };
    }
    // Fallback to latest
    return { version: findLatestVersion(versions), isFallback: true };
  }

  // Direct version ID
  const version = versions.find((v) => v.id === value);
  return { version, isFallback: false };
}
